import { create } from 'zustand';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { alertasService } from '../services/alertas-service';
import type { Alerta } from '../types/alertas.types';

/**
 * L5 (vault 278): el contador y la lista de alertas, compartidos entre el
 * encabezado de Precios Dinámicos y el panel de Monitoreo. «Vista» vive en el
 * servidor, por persona; acá no se guarda nada en el navegador.
 */
const PAGINA = 50;
/** El tope del servidor: un refresco no trae más que esto aunque se haya pedido «ver más». */
const TOPE = 200;

interface AlertasState {
  /** null = sin dato (no llegó o falló). Nunca se muestra como 0. */
  noVistas: number | null;
  ultimas: Alerta[];
  lista: Alerta[];
  cargando: boolean;
  /** No se pudo cargar la lista. */
  error: string | null;
  /** No se pudo marcar: aparte, para que recargar la lista no lo borre. */
  errorAlMarcar: string | null;
  /** «Marcar todas» en curso. */
  marcando: boolean;
  /** La última página vino llena: puede haber más viejas. */
  hayMas: boolean;
  cargandoMas: boolean;
  /** La tienda por la que se filtra la lista (desde «N cambios →»), o null. */
  filtroTienda: string | null;
  cargarResumen: () => Promise<void>;
  cargarLista: (opts?: { storeId?: string | null }) => Promise<void>;
  verMas: () => Promise<void>;
  /** Refresco silencioso de lista y contador (lo llama Monitoreo al refrescar). */
  refrescar: () => Promise<void>;
  marcarVista: (id: string) => Promise<void>;
  marcarTodas: () => Promise<void>;
}

export const useAlertasStore = create<AlertasState>((set, get) => ({
  noVistas: null,
  ultimas: [],
  lista: [],
  cargando: false,
  error: null,
  errorAlMarcar: null,
  marcando: false,
  hayMas: false,
  cargandoMas: false,
  filtroTienda: null,

  cargarResumen: async () => {
    try {
      const r = await alertasService.resumen();
      set({ noVistas: r.noVistas, ultimas: r.ultimas });
    } catch {
      set({ noVistas: null, ultimas: [] });
    }
  },

  cargarLista: async (opts = {}) => {
    const filtroTienda = opts.storeId ?? null;
    set({ cargando: true, error: null, filtroTienda });
    try {
      const lista = await alertasService.listar({
        limit: PAGINA,
        storeId: filtroTienda ?? undefined,
      });
      set({ lista, cargando: false, hayMas: lista.length === PAGINA });
    } catch (err) {
      set({ cargando: false, error: mensajeDeError(err, 'No se pudieron cargar las alertas.') });
    }
  },

  verMas: async () => {
    const { lista, cargandoMas, filtroTienda } = get();
    const ultima = lista[lista.length - 1];
    if (!ultima || cargandoMas) return;
    set({ cargandoMas: true });
    try {
      const mas = await alertasService.listar({
        limit: PAGINA,
        antesDe: ultima.createdAt,
        antesDeId: ultima.id,
        storeId: filtroTienda ?? undefined,
      });
      set((s) => {
        const ya = new Set(s.lista.map((a) => a.id));
        return {
          lista: [...s.lista, ...mas.filter((a) => !ya.has(a.id))],
          hayMas: mas.length === PAGINA,
          cargandoMas: false,
        };
      });
    } catch (err) {
      set({
        cargandoMas: false,
        error: mensajeDeError(err, 'No se pudieron cargar más alertas.'),
      });
    }
  },

  refrescar: async () => {
    const { filtroTienda, lista } = get();
    // Sin «cargando»: el panel no parpadea. Trae tantas como se están viendo.
    const limit = Math.min(TOPE, Math.max(PAGINA, lista.length));
    const [nueva] = await Promise.all([
      alertasService.listar({ limit, storeId: filtroTienda ?? undefined }).catch(() => null),
      get().cargarResumen(),
    ]);
    // Si falló, queda lo que se veía: un refresco de fondo no tapa la lista con un error.
    if (nueva) set({ lista: nueva, hayMas: nueva.length === limit, error: null });
  },

  marcarVista: async (id) => {
    const antes = get().lista.find((a) => a.id === id);
    if (!antes || antes.vista) return;
    set((s) => ({
      errorAlMarcar: null,
      lista: s.lista.map((a) => (a.id === id ? { ...a, vista: true } : a)),
      noVistas: s.noVistas != null ? Math.max(0, s.noVistas - 1) : s.noVistas,
    }));
    try {
      await alertasService.marcarVista(id);
    } catch (err) {
      set({ errorAlMarcar: mensajeDeError(err, 'No se pudo marcar la alerta como vista.') });
      await Promise.all([
        get().cargarLista({ storeId: get().filtroTienda }),
        get().cargarResumen(),
      ]);
    }
  },

  marcarTodas: async () => {
    const { lista, marcando, filtroTienda } = get();
    const masNueva = lista[0];
    if (!masNueva || marcando) return;
    set({ marcando: true, errorAlMarcar: null });
    try {
      await alertasService.marcarTodas(masNueva.createdAt);
    } catch (err) {
      set({ errorAlMarcar: mensajeDeError(err, 'No se pudieron marcar las alertas como vistas.') });
    }
    await Promise.all([get().cargarLista({ storeId: filtroTienda }), get().cargarResumen()]);
    set({ marcando: false });
  },
}));
